import React, { useEffect, useRef, useState, cloneElement } from "react";
export const ObscuredImage = ({
  src,
  alt = "",
  text = "",
  mode = "blur", // 'blur' | 'pixelate'
  blur = 1.5, // px
  pixelSize = 12, // larger = more pixelated
  style = {},
  className = "",
}) => {
  const safeSrc = src || "";

  const imgRef = useRef(null);
  const canvasRef = useRef(null);
  const [cssFallback, setCssFallback] = useState(false);

  // Draw pixelated mosaic onto canvas (robust across browsers)
  useEffect(() => {
    if (mode !== "pixelate") return;
    const img = imgRef.current;
    const canvas = canvasRef.current;
    if (!img || !canvas) return;

    const draw = () => {
      try {
        if (!img.naturalWidth || !img.naturalHeight) return;
        const smallW = Math.max(1, Math.floor(img.naturalWidth / pixelSize));
        const smallH = Math.max(1, Math.floor(img.naturalHeight / pixelSize));

        const off = document.createElement("canvas");
        off.width = smallW;
        off.height = smallH;
        const offCtx = off.getContext("2d");
        offCtx.imageSmoothingEnabled = false;
        offCtx.drawImage(img, 0, 0, smallW, smallH);

        const ctx = canvas.getContext("2d");
        ctx.imageSmoothingEnabled = false;
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        ctx.drawImage(
          off,
          0,
          0,
          smallW,
          smallH,
          0,
          0,
          canvas.width,
          canvas.height
        );
      } catch (e) {
        setCssFallback(true);
      }
    };

    if (img.complete) draw();
    else img.addEventListener("load", draw, { once: true });
  }, [mode, pixelSize, safeSrc]);

  // Base wrapper (keeps layout from inline <img>)
  const baseStyle = {
    position: "relative",
    display: "block",
    width: "100%",
    marginBottom: "22px",
    ...style,
  };

  // Visual layer: blurred <img> or reliable pixelation by shrinking then scaling
  const visualLayer =
    mode === "pixelate" ? (
      <>
        {/* Base image (hidden) for natural ratio */}
        <img
          ref={imgRef}
          src={safeSrc}
          alt={alt}
          crossOrigin="anonymous"
          style={{ width: "100%", display: "block" }}
        />
        {cssFallback ? (
          <img
            src={safeSrc}
            alt=""
            aria-hidden
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: `${100 / Math.max(1, pixelSize)}%`,
              height: "auto",
              transform: `scale(${Math.max(1, pixelSize)})`,
              transformOrigin: "top left",
              imageRendering: "pixelated",
              pointerEvents: "none",
              display: "block",
            }}
          />
        ) : (
          <canvas
            ref={canvasRef}
            aria-hidden
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
              display: "block",
            }}
          />
        )}
      </>
    ) : (
      <img
        src={safeSrc}
        alt={alt}
        style={{ width: "100%", display: "block", filter: `blur(${blur}px)` }}
      />
    );

  return (
    <div className={className} style={baseStyle}>
      {visualLayer}
      {/* Centered overlay text */}
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "8px 12px",
        }}
      >
        <span
          style={{
            display: "inline-block",
            maxWidth: "90%",
            background: "rgba(0,0,0,0.45)",
            color: "#fff",
            fontSize: "16px",
            lineHeight: 1.35,
            padding: "8px 12px",
            borderRadius: 8,
            textAlign: "center",
            wordBreak: "keep-all",
          }}
        >
          {text}
        </span>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Non-breaking wrapper: keeps the original <img> element intact
// Usage:
//   <Obscure text="비공개" mode="blur" blur={14}>
//     <img src={...} alt={...} style={{ width: '80%', marginBottom: '22px' }} />
//   </Obscure>
// Only visual styles are merged into the child; src/alt/className 등은 변경하지 않음.
export const Obscure = ({
  children,
  text = "",
  mode = "blur", // 'blur' | 'pixelate'
  blur = 1.5, // px (mode='blur')
  pixelSize = 12, // (mode='pixelate')
  className = "",
  style = {},
}) => {
  // Guard: expect a single React element (ideally an <img>)
  const child = Array.isArray(children) ? children[0] : children;
  const isElement = child && typeof child === "object" && "props" in child;

  // Wrapper preserves layout
  const wrapStyle = {
    position: "relative",
    display: "block",
    width: "100%",
    ...style,
  };

  const baseImgRef = useRef(null);
  const overlayCanvasRef = useRef(null);

  // If child is an <img>, either blur it minimally or overlay a pixelated copy
  let rendered = child;
  let pixelOverlay = null;
  if (isElement && child.type === "img") {
    const base = child.props?.style || {};
    if (mode === "pixelate") {
      // Keep original for layout but hide it visually
      rendered = {
        ...child,
        props: {
          ...child.props,
          ref: baseImgRef,
          style: { ...base },
        },
      };
      pixelOverlay = (
        <canvas
          ref={overlayCanvasRef}
          aria-hidden
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            display: "block",
          }}
        />
      );
    } else {
      rendered = cloneElement(child, {
        style: { ...base, filter: `blur(${blur}px)` },
      });
    }
  }

  // Draw pixelation for HOC on load/prop changes
  useEffect(() => {
    if (mode !== "pixelate") return;
    const img = baseImgRef.current;
    const canvas = overlayCanvasRef.current;
    if (!img || !canvas) return;

    const draw = () => {
      if (!img.naturalWidth || !img.naturalHeight) return;
      const smallW = Math.max(1, Math.floor(img.naturalWidth / pixelSize));
      const smallH = Math.max(1, Math.floor(img.naturalHeight / pixelSize));

      const off = document.createElement("canvas");
      off.width = smallW;
      off.height = smallH;
      const offCtx = off.getContext("2d");
      offCtx.imageSmoothingEnabled = false;
      offCtx.drawImage(img, 0, 0, smallW, smallH);

      const ctx = canvas.getContext("2d");
      ctx.imageSmoothingEnabled = false;
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      ctx.drawImage(
        off,
        0,
        0,
        smallW,
        smallH,
        0,
        0,
        canvas.width,
        canvas.height
      );
    };

    if (img.complete) draw();
    else img.addEventListener("load", draw, { once: true });
  }, [mode, pixelSize, child]);

  return (
    <div className={className} style={wrapStyle}>
      {rendered}
      {pixelOverlay}
      {/* Centered overlay text */}
      {text ? (
        <div
          style={{
            position: "absolute",
            inset: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none", // 클릭 이벤트는 원본 이미지로 전달
            padding: "8px 12px",
          }}
        >
          <span
            style={{
              display: "inline-block",
              maxWidth: "90%",
              background: "rgba(0,0,0,0.45)",
              color: "#fff",
              fontSize: "16px",
              lineHeight: 1.35,
              padding: "8px 12px",
              borderRadius: 8,
              textAlign: "center",
              wordBreak: "keep-all",
            }}
          >
            {text}
          </span>
        </div>
      ) : null}
    </div>
  );
};
